import { useEffect, useRef, useState } from 'react';

/**
 * Debounced autosave.
 *
 * Fires `save()` `delay` ms after the watched payload stops changing.
 * Disabled documents (submitted / approved / signed) pass
 * `enabled: false` — the payload is then only tracked as the new
 * baseline so re-enabling never replays stale edits.
 *
 * Returns 'idle' | 'saving' | 'saved' | 'error' for the UI indicator.
 */
export default function useAutosave(payload, save, { enabled = true, delay = 1500 } = {}) {
  const [state, setState] = useState('idle');
  const serialised = JSON.stringify(payload ?? null);
  const baseline = useRef(serialised);
  const timer = useRef(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (!enabled) { baseline.current = serialised; return undefined; }
    if (serialised === baseline.current) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setState('saving');
      try {
        await saveRef.current();
        baseline.current = serialised;
        setState('saved');
      } catch (_e) {
        setState('error');
      }
    }, delay);
    return () => clearTimeout(timer.current);
  }, [serialised, enabled, delay]);

  return state;
}

export const AUTOSAVE_LABEL = {
  idle: '',
  saving: 'Saving…',
  saved: 'Saved ✓',
  error: 'Autosave failed — use Save',
};
